import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotificationPopup from '../../utility/NotificationPopup';
import BootstrapDatePicker from '../utility/BootstrapDatePicker';
import useApplicationStore from '../../store/applicationStore';
import { CREDIT_API_URL, TRUSTPAISA_URL, CASHE_URL } from '../../Constants';
import '../../assets/css/application.css';


const EligibilityFormSection = () => {
  const navigate = useNavigate();
  
  // Zustand store
  const { leadData: storeLeadData, setLeadData: setStoreLeadData } = useApplicationStore();
  
  // Calculate default date (22 years ago) in DD/MM/YYYY format
  const getDefaultDate = () => {
    const today = new Date();
    const defaultDate = new Date(today.getFullYear() - 22, today.getMonth(), today.getDate());
    const day = String(defaultDate.getDate()).padStart(2, '0');
    const month = String(defaultDate.getMonth() + 1).padStart(2, '0');
    const year = defaultDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Parse DD/MM/YYYY format to Date object for calculations
  const parseDateForCalculation = (dateString) => {
    if (!dateString) return null;
    
    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }
    
    // Handle YYYY-MM-DD format (fallback)
    return new Date(dateString);
  };

  const [formData, setFormData] = useState({
    dateOfBirth: getDefaultDate(),
    profession: 'salaried',
    companyName: '',
    monthlyIncome: '',
    salaryMode: 'direct-bank',
    consent: false
  });

  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState('');
  const [leadData, setLeadData] = useState(null);
  const [loanAmount, setLoanAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success',
    redirectUrl: null,
    showLoader: false,
    lenderName: null,
    openInNewTab: false
  });

  // Load lead data from Zustand store on component mount
  useEffect(() => {
    if (storeLeadData) {
      setLeadData(storeLeadData);
    }
    
    // Also check sessionStorage for loan amount as fallback
    const storedLoanAmount = sessionStorage.getItem('loanAmount');
    if (storedLoanAmount) {
      setLoanAmount(parseInt(storedLoanAmount));
    }
  }, [storeLeadData]);

  // Check if form is valid for enabling/disabling submit button
  const isFormValid = () => {
    // Check if all required fields are filled
    const hasAllFields = 
      formData.dateOfBirth && 
      formData.profession && 
      formData.companyName && 
      formData.monthlyIncome && 
      formData.salaryMode && 
      formData.consent;

    // Check if there are no validation errors
    const hasNoErrors = Object.keys(errors).length === 0;

    // Check if monthly income meets minimum requirement
    const hasValidIncome = formData.monthlyIncome && parseInt(formData.monthlyIncome) >= 10000;

    // Check if age is within valid range (22-55 years)
    let hasValidAge = false;
    if (formData.dateOfBirth) {
      const today = new Date();
      const birthDate = parseDateForCalculation(formData.dateOfBirth);
      if (birthDate && !isNaN(birthDate.getTime())) {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        hasValidAge = age >= 22 && age <= 55;
      }
    }

    return hasAllFields && hasNoErrors && hasValidIncome && hasValidAge;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Real-time validation for each field
    validateField(name, type === 'checkbox' ? checked : processedValue);

    // Check if user selected non-eligible options
    if (name === 'profession' && value !== 'salaried' && value !== 'self-employed') {
      setSelectedProfession(value);
      setShowModal(true);
    }
  };

  // Real-time field validation
  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'dateOfBirth':
        if (!value) {
          newErrors.dateOfBirth = 'Date of Birth is required';
        } else {
          const today = new Date();
          const birthDate = parseDateForCalculation(value);
          if (birthDate && !isNaN(birthDate.getTime())) {
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            
            if (age < 22 || age > 55) {
              newErrors.dateOfBirth = 'Age must be between 22 to 55 years';
            } else {
              delete newErrors.dateOfBirth;
            }
          } else {
            newErrors.dateOfBirth = 'Please enter a valid date';
          }
        }
        break;

      case 'profession':
        if (!value) {
          newErrors.profession = 'Please select your profession';
        } else {
          delete newErrors.profession;
        }
        break;

      case 'companyName':
        if (!value || value.trim().length === 0) {
          newErrors.companyName = 'Company name is required';
        } else if (value.trim().length < 2) {
          newErrors.companyName = 'Company name must be at least 2 characters';
        } else {
          delete newErrors.companyName;
        }
        break;

      case 'monthlyIncome':
        if (!value) {
          newErrors.monthlyIncome = 'Monthly income is required';
        } else if (parseInt(value) < 10000) {
          newErrors.monthlyIncome = 'Monthly income should be greater than or equal to ₹10,000';
        } else {
          delete newErrors.monthlyIncome;
        }
        break;

      case 'salaryMode':
        if (!value) {
          newErrors.salaryMode = 'Please select how you receive your salary';
        } else {
          delete newErrors.salaryMode;
        }
        break;

      case 'consent':
        if (!value) {
          newErrors.consent = 'Please accept the terms and conditions';
        } else {
          delete newErrors.consent;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleUnderstandClick = () => {
    setShowModal(false);
    setSelectedProfession('');
    navigate('/');
  };

  const getModalMessage = () => {
    return `Thank you for your interest! We will not be able to proceed further with your application. Currently we are only offering loans to salaried and self-employed individuals.`;
  };

  const handleCancelClick = () => {
    setShowModal(false);
    setSelectedProfession('');
    // Reset to default values
    setFormData(prev => ({
      ...prev,
      profession: 'salaried'
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Date of Birth validation (22 to 55 years)
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of Birth is required';
    } else {
      const today = new Date();
      const birthDate = parseDateForCalculation(formData.dateOfBirth);
      if (birthDate && !isNaN(birthDate.getTime())) {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        if (age < 22 || age > 55) {
          newErrors.dateOfBirth = 'Age must be between 22 to 55 years';
        }
      } else {
        newErrors.dateOfBirth = 'Please enter a valid date';
      }
    }

    // Profession validation
    if (!formData.profession) {
      newErrors.profession = 'Please select your profession';
    }

    // Company Name validation
    if (!formData.companyName || formData.companyName.trim().length === 0) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }

    // Monthly Income validation
    if (!formData.monthlyIncome) {
      newErrors.monthlyIncome = 'Monthly income is required';
    } else if (parseInt(formData.monthlyIncome) < 10000) {
      newErrors.monthlyIncome = 'Monthly income should be greater than or equal to ₹10,000';
    }

    // Salary Mode validation
    if (!formData.salaryMode) {
      newErrors.salaryMode = 'Please select how you receive your salary';
    }

    // Consent validation
    if (!formData.consent) {
      newErrors.consent = 'Please accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      if (!leadData || !leadData.id) {
        console.error('No lead data found');
        return;
      }

      setIsProcessing(true);
      try {
        // Convert DD/MM/YYYY to YYYY-MM-DD for API
        const convertDateForAPI = (dateString) => {
          if (!dateString) return '';
          if (dateString.includes('/')) {
            const parts = dateString.split('/');
            if (parts.length === 3) {
              const day = parts[0];
              const month = parts[1];
              const year = parts[2];
              return `${year}-${month}-${day}`;
            }
          }
          return dateString; // Return as-is if already in correct format
        };

        // Call eligibility check API
        const eligibilityPayload = {
          leadId: leadData.id,
          dateOfBirth: convertDateForAPI(formData.dateOfBirth), // Convert DD/MM/YYYY to YYYY-MM-DD
          employmentType: formData.profession === 'salaried' ? 'Salaried' : 'Self Employed',
          companyName: formData.companyName.trim(),
          salaryReceivedType: formData.salaryMode === 'direct-bank' ? 'Direct Bank Account' : 
                             formData.salaryMode === 'cheque' ? 'Cheque' : 'Cash',
          monthlySalary: parseInt(formData.monthlyIncome)
        };

        console.log('Eligibility payload:', eligibilityPayload);

        const response = await axios.post(`${CREDIT_API_URL}/eligibility/check`, eligibilityPayload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        console.log('Eligibility response:', response.data);

        if (response.data.eligible) {
          // Update leadData in Zustand store with eligibility information
          setStoreLeadData({ ...leadData, eligibility: response.data });
          // User is eligible, show notification with redirect based on lenderId
          const lenderId = response.data.lenderId;
          const lenderName = response.data.lenderName;
          const lenderLink = response.data.lenderLink;
          
          let notificationData;
          
          if (lenderId === 'CASHE_001') {
            // Redirect to internal application journey
            notificationData = {
              show: true,
              message: `Great! 🙂 You meet our lender's basic criteria. `,
              type: 'success',
              redirectUrl: '/application/journey',
              showLoader: true,
              lenderName: 'cashe',
              openInNewTab: false
            };
          } else if (lenderLink) {
            // For all other lenders, open in new tab
            notificationData = {
              show: true,
              message: `Great! 🙂 You meet our lender's basic criteria.`,
              type: 'success',
              redirectUrl: lenderLink,
              showLoader: true,
              lenderName: lenderName?.toLowerCase().replace(/\s+/g, ''),
              openInNewTab: true
            };
          } else {
            // Fallback notification
            notificationData = {
              show: true,
              message: `Thank you for your interest! We will not be able to proceed further with your application.`,
              type: 'success',
              redirectUrl: null,
              showLoader: false,
              lenderName: null,
              openInNewTab: false
            };
          }

          setNotification(notificationData);
          
          // Clear session storage
          sessionStorage.removeItem('loanAmount');
        } else {
          // User is not eligible
          setNotification({
            show: true,
            message: `Thank you for your interest! We will not be able to proceed further with your application.`,
            type: 'error',
            redirectUrl: null,
            showLoader: false,
            lenderName: null
          });
        }
      } catch (error) {
        console.error('Error checking eligibility:', error);
        setNotification({
          show: true,
          message: 'An error occurred while checking eligibility. Please try again.',
          type: 'error',
          redirectUrl: null,
          showLoader: false,
          lenderName: null
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="application-section eligibility-form-section">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            {!leadData ? (
              <div className="application-container eligibility-form-container">
                <div className="text-center p-4">
                  <h3>No Lead Data Found</h3>
                  <p>Please start your application from the homepage first.</p>
                  <Button 
                    onClick={() => navigate('/')}
                    className="emi-homepage-start-application-btn"
                  >
                    Go to Homepage
                  </Button>
                </div>
              </div>
            ) : (
              <div className="application-container eligibility-form-container">
              <Form onSubmit={handleSubmit}>
                {/* Date of Birth */}
                <Row className="mb-4 align-items-center">
                  <Col md={5} sm={12} className="mb-3 mb-md-0">
                    <label className="form-label">
                      Enter your Date of Birth
                    </label>
                  </Col>
                  <Col md={7} sm={12}>
                    <BootstrapDatePicker
                      name="dateOfBirth"
                      id="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      placeholder="Select your date of birth"
                      maxDate={new Date().toISOString().split('T')[0]}
                      error={errors.dateOfBirth}
                      className="eligibility-datepicker"
                    />
                  </Col>
                </Row>

                {/* Profession */}
                <Row className="mb-4">
                  <Col md={5} sm={12} className="mb-3 mb-md-0">
                    <label className="form-label">
                      What is your current profession?
                    </label>
                  </Col>
                  <Col md={7} sm={12}>
                    <div className="radio-group vertical">
                      {['salaried', 'business', 'self-employed', 'other'].map((option) => (
                        <div key={option} className="radio-option">
                          <input
                            type="radio"
                            id={`profession-${option}`}
                            name="profession"
                            value={option}
                            checked={formData.profession === option}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <label htmlFor={`profession-${option}`} className="radio-label">
                            {option === 'salaried' ? 'Salaried' : 
                             option === 'business' ? 'Business' : 
                             option === 'self-employed' ? 'Self Employed' : 'Other'}
                          </label>
                        </div>
                      ))}
                    </div>
                    {errors.profession && (
                      <div className="emi-homepage-validation-error">{errors.profession}</div>
                    )}
                  </Col>
                </Row>

                {/* Company Name */}
                <Row className="mb-4 align-items-center">
                  <Col md={5} sm={12} className="mb-3 mb-md-0">
                    <label className="form-label">
                      In which company are you working?
                    </label>
                  </Col>
                  <Col md={7} sm={12}>
                    <div className="emi-homepage-input-field-container">
                      <div className={`emi-homepage-input-field ${errors.companyName ? 'emi-homepage-input-field-error' : ''}`}>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          className="emi-homepage-input-field-text"
                          placeholder="Enter company name"
                        />
                      </div>
                    </div>
                    {errors.companyName && (
                      <div className="emi-homepage-validation-error">{errors.companyName}</div>
                    )}
                  </Col>
                </Row>

                {/* Monthly Income */}
                <Row className="mb-4 align-items-center">
                  <Col md={5} sm={12} className="mb-3 mb-md-0">
                    <label className="form-label">
                      What is your current monthly income?
                    </label>
                  </Col>
                  <Col md={7} sm={12}>
                    <div className="emi-homepage-input-field-container">
                      <div className={`emi-homepage-input-field ${errors.monthlyIncome ? 'emi-homepage-input-field-error' : ''}`}>
                        <span className="emi-homepage-input-prefix">₹</span>
                        <input
                          type="number"
                          name="monthlyIncome"
                          value={formData.monthlyIncome}
                          onChange={handleChange}
                          className="emi-homepage-input-field-text"
                          placeholder="Monthly income"
                          min="10000"
                        />
                      </div>
                    </div>
                    {errors.monthlyIncome && (
                      <div className="emi-homepage-validation-error">{errors.monthlyIncome}</div>
                    )}
                  </Col>
                </Row>

                {/* Salary Mode */}
                <Row className="mb-4">
                  <Col md={5} sm={12} className="mb-3 mb-md-0">
                    <label className="form-label">
                      How do you receive your salary?
                    </label>
                  </Col>
                  <Col md={7} sm={12}>
                    <div className="radio-group vertical">
                      {['direct-bank', 'cheque', 'cash'].map((option) => (
                        <div key={option} className="radio-option">
                          <input
                            type="radio"
                            id={`salaryMode-${option}`}
                            name="salaryMode"
                            value={option}
                            checked={formData.salaryMode === option}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <label htmlFor={`salaryMode-${option}`} className="radio-label">
                            {option === 'direct-bank' ? 'Direct Bank Account' : 
                             option === 'cheque' ? 'Cheque' : 'Cash'}
                          </label>
                        </div>
                      ))}
                    </div>
                    {errors.salaryMode && (
                      <div className="emi-homepage-validation-error">{errors.salaryMode}</div>
                    )}
                  </Col>
                </Row>

                {/* T&C Consent - Centered */}
                <Row className="justify-content-center">
                  <Col md={8} sm={12} >
                    <div className="homepage-consent-section" style={{ alignItems: 'center' }}>
                      <div className={`homepage-consent-content ${errors.consent ? 'homepage-consent-section-error' : ''}`}>
                        <input
                          type="checkbox"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleChange}
                          className="homepage-consent-checkbox"
                        />
                        <span className="homepage-consent-text">
                          I agree to the {' '}<Link to="/privacy-policy" className="privacy-link">Terms & Conditions</Link> and{' '}
                          <Link to="/privacy-policy" className="privacy-link">
                            Privacy Policy
                          </Link>
                        </span>
                      </div>
                      {errors.consent && (
                        <div className="homepage-consent-error">
                          <div className="emi-homepage-validation-error">{errors.consent}</div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Submit Button - Centered */}
                <Row className="mb-4 justify-content-center">
                  <Col md={8} sm={12} className="text-center">
                    <Button 
                      type="submit" 
                      className="emi-homepage-start-application-btn"
                      style={{ minWidth: '200px', margin: '0 auto' }}
                      disabled={isProcessing || !isFormValid()}
                    >
                      {isProcessing ? 'Checking Eligibility...' : 'Check Eligibility'}
                    </Button>
                  </Col>
                </Row>
              </Form>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Eligibility Modal */}
      <Modal show={showModal} onHide={handleCancelClick} centered>
        <Modal.Body className="text-center p-4">
          <div className="warning-icon mb-3">
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ffc107' }}></i>
          </div>
          <p className="mb-4" style={{ fontSize: '16px', color: '#333' }}>
            {getModalMessage()}
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button 
              variant="outline-secondary" 
              onClick={handleCancelClick}
              style={{ minWidth: '120px' }}
            >
              Cancel
            </Button>
            <Button 
              variant="warning" 
              onClick={handleUnderstandClick}
              style={{ minWidth: '120px' }}
            >
              I Understand
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      
      {/* Notification Popup */}
      <NotificationPopup
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ 
          show: false, 
          message: '', 
          type: 'success',
          redirectUrl: null,
          showLoader: false,
          lenderName: null,
          openInNewTab: false
        })}
        redirectUrl={notification.redirectUrl}
        showLoader={notification.showLoader}
        lenderName={notification.lenderName}
        openInNewTab={notification.openInNewTab}
      />
    </div>
  );
};

export default EligibilityFormSection;
 